import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

Swiper.use([Navigation, Pagination, Autoplay]);

export function initSwipers(): void {
  // W53 - Content Carousel (centered slides with scale effect)
  const contentCarousels = document.querySelectorAll('.swiper--carousel') as NodeListOf<HTMLElement>;
  contentCarousels.forEach((carousel) => {
    // Click vs drag detection for link navigation
    let dragStart = false;
    let clickStart = { x: 0, y: 0 };

    carousel.addEventListener('mousedown', (e) => {
      dragStart = true;
      clickStart = { x: e.clientX, y: e.clientY };
    });

    carousel.addEventListener('mouseup', (e) => {
      const clickEnd = { x: e.clientX, y: e.clientY };
      const distance = Math.sqrt(
        Math.pow(clickEnd.x - clickStart.x, 2) + Math.pow(clickEnd.y - clickStart.y, 2)
      );

      // If moved more than 5px, it's a drag - prevent link navigation
      if (dragStart && distance > 5) {
        const link = (e.target as HTMLElement).closest('.swiper-slide-link') as HTMLAnchorElement;
        if (link) {
          e.preventDefault();
        }
      }
      dragStart = false;
    });

    const swiper = new Swiper(carousel, {
      modules: [],
      loop: false,
      centeredSlides: true,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 20,
      breakpoints: {
        576: { spaceBetween: 30 },
        768: { spaceBetween: 40 },
        992: { spaceBetween: 50 },
      },
      grabCursor: true,
      watchSlidesProgress: true,
      on: {
        progress: function(swiper) {
          // Dynamic scaling during drag based on slide position
          for (let i = 0; i < swiper.slides.length; i++) {
            const slide = swiper.slides[i] as HTMLElement;
            const slideProgress = (slide as any).progress;
            const absProgress = Math.abs(slideProgress);

            // Scale from 0.75 to 1 based on distance from center
            // 0 = center (scale 1), 1+ = far (scale 0.75)
            const scale = 1 - Math.min(absProgress, 1) * 0.25;

            const slideInner = slide.querySelector('div, a') as HTMLElement;
            if (slideInner) {
              slideInner.style.transform = `scale(${scale})`;
            }
          }
        },
        setTransition: function(swiper, transition) {
          for (let i = 0; i < swiper.slides.length; i++) {
            const slide = swiper.slides[i] as HTMLElement;
            const slideInner = slide.querySelector('div, a') as HTMLElement;
            if (slideInner) {
              slideInner.style.transition = `transform ${transition}ms ease`;
            }
          }
        },
      },
    });

    // Manual navigation button handling to ensure single-slide movement
    const nextBtn = carousel.querySelector('.swiper-button-next') as HTMLElement;
    const prevBtn = carousel.querySelector('.swiper-button-prev') as HTMLElement;

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        swiper.slideNext();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        swiper.slidePrev();
      });
    }
  });

  // W30 - Gallery Carousel (image gallery slider with pagination and navigation)
  const galleryCarousels = document.querySelectorAll('.gallery-carousel') as NodeListOf<HTMLElement>;
  galleryCarousels.forEach((carousel) => {
    const paginationEl = carousel.querySelector('.swiper-pagination') as HTMLElement | null;

    new Swiper(carousel, {
      modules: [Navigation, Pagination],
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: '.gallery-nav-next',
        prevEl: '.gallery-nav-prev',
      },
      pagination: paginationEl ? {
        el: paginationEl,
        type: 'bullets',
        clickable: true,
      } : false,
    });
  });

  // W54 - Marquee (autoplay, slider effect)
  const marquees = document.querySelectorAll('.swiper--marquee') as NodeListOf<HTMLElement>;
  marquees.forEach((marquee) => {
    const slideCount = marquee.querySelectorAll('.swiper-slide').length;

    new Swiper(marquee, {
      modules: [Autoplay],
      slidesPerView: 1,
      breakpoints: {
        576: { slidesPerView: 2 }, // sm
        768: { slidesPerView: 3 }, // md
        992: { slidesPerView: 4 }, // lg
        1200: { slidesPerView: 5 }, // xl
        1400: { slidesPerView: 5 }, // xxl
      },
      loop: slideCount > 1,
      spaceBetween: 48,
      speed: 8000,
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },
    });
  });

  // W26 - Testimonial Slider (Horizontal) - support multiple instances
  const horizontalSliders = document.querySelectorAll('[class*="testimonial-swiper-"]') as NodeListOf<HTMLElement>;
  horizontalSliders.forEach((slider) => {
    const sliderId = slider.className.match(/testimonial-swiper-([a-zA-Z0-9]+)/)?.[1];
    const slideCount = parseInt(slider.dataset.slideCount || '0', 10);

    if (sliderId && slideCount >= 2) {
      new Swiper(slider, {
        modules: [Navigation],
        slidesPerView: 1,
        loop: slideCount >= 3,
        navigation: {
          nextEl: `.next-btn-${sliderId}`,
          prevEl: `.prev-btn-${sliderId}`,
        },
      });
    }
  });

  // W27 - Testimonial Slider (Vertical) - support multiple instances
  const verticalSliders = document.querySelectorAll('[class*="vertical-swiper-"]') as NodeListOf<HTMLElement>;
  verticalSliders.forEach((slider) => {
    const sliderId = slider.className.match(/vertical-swiper-([a-zA-Z0-9]+)/)?.[1];
    const slideCount = parseInt(slider.dataset.slideCount || '0', 10);

    if (sliderId && slideCount >= 2) {
      new Swiper(slider, {
        modules: [Navigation],
        slidesPerView: 1,
        loop: slideCount >= 3,
        autoHeight: true,
        navigation: {
          nextEl: `.next-btn-vertical-${sliderId}`,
          prevEl: `.prev-btn-vertical-${sliderId}`,
        },
      });
    }
  });
}
